import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex, Spinner, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getOidcUserManager } from '../../services/oidcClient';

/** Type guard for the OIDC state payload stored by LoginPage. */
function extractFromPath(state: unknown): string | undefined {
  if (state !== null && typeof state === 'object' && 'from' in state && typeof (state as { from: unknown }).from === 'string') {
    return (state as { from: string }).from;
  }
  return undefined;
}

/**
 * OIDC redirect target. Exchanges the authorization code for tokens via the
 * UserManager, then bounces to the originally requested route (stored in OIDC
 * state by LoginPage) or /dashboard as a fallback.
 */
export function CallbackPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const bgColor = useColorModeValue('secondaryGray.300', 'navy.900');

  useEffect(() => {
    let cancelled = false;
    getOidcUserManager()
      .then((mgr) => mgr.signinRedirectCallback())
      .then((user) => {
        if (cancelled) return;
        // The AuthProvider's UserLoaded event picks up the new session and
        // syncs the admin profile; AuthGuard then routes the user to the
        // protected dashboard. We just need to leave the /callback URL.
        const redirectTo = extractFromPath(user.state) ?? '/dashboard';
        navigate(redirectTo, { replace: true });
      })
      .catch((e: Error) => {
        if (cancelled) return;
        // Callback failures are often user-triggered (refreshing /callback,
        // stale state, etc.) — log as a warning, not an error.
        console.warn('[AdminAuth] OIDC callback failed', e);
        setError(t('admin.auth.errors.callbackFailed'));
      });
    return () => { cancelled = true; };
  }, [navigate, t]);

  return (
    <Flex justify="center" align="center" minH="100vh" bg={bgColor} data-testid="oidc-callback">
      <VStack>
        {error
          ? <Text color="red.500" data-testid="oidc-callback-error">{error}</Text>
          : <><Spinner size="xl" color="brand.500" thickness="3px" /><Text mt={4}>Signing you in…</Text></>}
      </VStack>
    </Flex>
  );
}
